import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { API_BASE_URL } from "../../config/apiBase";
import { getAuthUserHeader } from "../../utils/userRoles";
import AdminShell from "./AdminShell";

const API = API_BASE_URL;

function titleize(value) {
  return String(value || "")
    .replace(/[_-]+/g, " ")
    .replace(/\b\w/g, (m) => m.toUpperCase())
    .trim();
}

function formatDate(value) {
  if (!value) return "Never";
  const dt = new Date(value);
  return Number.isNaN(dt.getTime()) ? String(value) : dt.toLocaleString();
}

export default function AdminUsersPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [items, setItems] = useState([]);
  const [roles, setRoles] = useState(["all", "admin", "tutor", "student", "tester"]);
  const [search, setSearch] = useState("");
  const [roleFilter, setRoleFilter] = useState("all");
  const [activeFilter, setActiveFilter] = useState("all");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState("");
  const [createForm, setCreateForm] = useState({
    full_name: "",
    email: "",
    password: "",
    confirm_password: "",
    role: "student",
    is_active: true,
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [message, setMessage] = useState("");
  const mountedRef = useRef(false);
  const requestSeq = useRef(0);

  const load = useCallback(async () => {
    const requestId = ++requestSeq.current;
    try {
      setLoading(true);
      setError("");
      const params = new URLSearchParams();
      if (search.trim()) params.set("search", search.trim());
      if (roleFilter && roleFilter !== "all") params.set("role", roleFilter);
      if (activeFilter && activeFilter !== "all") params.set("active", activeFilter);
      const res = await fetch(`${API}/api/admin/users${params.toString() ? `?${params.toString()}` : ""}`, {
        cache: "no-store",
        headers: { "X-User": getAuthUserHeader() },
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to load users");
      if (!mountedRef.current || requestId !== requestSeq.current) return;
      setItems(Array.isArray(data?.items) ? data.items : []);
      if (Array.isArray(data?.roles) && data.roles.length) {
        const nextRoles = ["all", ...data.roles];
        if (nextRoles.join("|") !== roles.join("|")) {
          setRoles(nextRoles);
        }
      }
    } catch (err) {
      if (!mountedRef.current || requestId !== requestSeq.current) return;
      setError(err?.message || "Failed to load users");
      setItems([]);
    } finally {
      if (mountedRef.current && requestId === requestSeq.current) setLoading(false);
    }
  }, [activeFilter, roleFilter, roles, search]);

  useEffect(() => {
    mountedRef.current = true;
    const timer = window.setTimeout(() => {
      load();
    }, 180);
    return () => {
      mountedRef.current = false;
      window.clearTimeout(timer);
    };
  }, [load]);

  useEffect(() => {
    if (location.state?.message) {
      setMessage(location.state.message);
      window.history.replaceState({}, document.title, window.location.href);
    }
  }, [location.state]);

  const summary = useMemo(() => {
    return {
      active: items.filter((item) => item.is_active).length,
      inactive: items.filter((item) => !item.is_active).length,
      admins: items.filter((item) => String(item.role || "").toLowerCase() === "admin").length,
    };
  }, [items]);

  const openCreateModal = () => {
    setCreateError("");
    setCreateForm({
      full_name: "",
      email: "",
      password: "",
      confirm_password: "",
      role: "student",
      is_active: true,
    });
    setShowCreateModal(true);
  };

  const closeCreateModal = () => {
    if (creating) return;
    setShowCreateModal(false);
    setCreateError("");
  };

  const submitCreateUser = async (event) => {
    event.preventDefault();
    if (creating) return;
    if (createForm.password !== createForm.confirm_password) {
      setCreateError("Passwords do not match.");
      return;
    }
    try {
      setCreating(true);
      setCreateError("");
      const res = await fetch(`${API}/api/admin/users`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-User": getAuthUserHeader(),
        },
        body: JSON.stringify(createForm),
      });
      const data = await res.json().catch(() => null);
      if (!res.ok) throw new Error((data && data.detail) || "Failed to create user");
      setShowCreateModal(false);
      await load();
    } catch (err) {
      setCreateError(err?.message || "Failed to create user");
    } finally {
      setCreating(false);
    }
  };

  return (
    <AdminShell title="Users" subtitle="Manage all accounts and access states.">
      <section className="td-card reveal" style={{ "--delay": ".08s", padding: 16, marginBottom: 16 }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 12 }}>
          {[
            ["Active", summary.active],
            ["Inactive", summary.inactive],
            ["Admins", summary.admins],
          ].map(([label, value]) => (
            <div key={label} style={{ padding: 14, borderRadius: 14, border: "1px solid rgba(255,255,255,.08)", background: "rgba(255,255,255,.03)" }}>
              <div style={{ fontSize: 12, color: "var(--muted, #b8b3aa)", marginBottom: 6 }}>{label}</div>
              <div style={{ fontSize: 24, fontWeight: 800 }}>{value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center" }}>
          <input
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Search name, email, or ID"
            aria-label="Search users"
            style={fieldStyle}
          />
          <select value={roleFilter} onChange={(event) => setRoleFilter(event.target.value)} style={fieldStyle}>
            {roles.map((role) => (
              <option key={role} value={role}>
                {role === "all" ? "All Roles" : titleize(role)}
              </option>
            ))}
          </select>
          <select value={activeFilter} onChange={(event) => setActiveFilter(event.target.value)} style={fieldStyle}>
            {[
              ["all", "All Statuses"],
              ["active", "Active"],
              ["inactive", "Inactive"],
            ].map(([value, label]) => (
              <option key={value} value={value}>
                {label}
              </option>
            ))}
          </select>
          <button type="button" onClick={() => load()} style={buttonStyle}>
            Refresh
          </button>
          <button type="button" onClick={openCreateModal} style={primaryButtonStyle}>
            Add User
          </button>
        </div>
      </section>

      {loading ? <div className="td-loading">Loading users…</div> : null}
      {error ? <div className="td-empty" style={{ color: "#ffb1aa" }}>{error}</div> : null}
      {message ? <div className="td-empty" style={{ color: "#9ee6a2" }}>{message}</div> : null}

      {!loading && !error && items.length === 0 ? (
        <div className="td-empty">No users found.</div>
      ) : null}

      {!loading && !error && items.length > 0 ? (
        <section className="td-card reveal" style={{ "--delay": ".1s", padding: 0, overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 980 }}>
            <thead>
              <tr style={{ textAlign: "left", color: "var(--muted, #b8b3aa)" }}>
                {["User ID / Student ID", "Name", "Email", "Role", "Status", "Last Login", ""].map((label) => (
                  <th key={label} style={thStyle}>
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.user_id} style={trStyle}>
                  <td style={tdStyle}>{item.student_id || item.user_id}</td>
                  <td style={tdStyle}>
                    <div style={{ fontWeight: 700 }}>{item.name || "Unnamed"}</div>
                  </td>
                  <td style={tdStyle}>{item.email || "—"}</td>
                  <td style={tdStyle}>{titleize(item.role)}</td>
                  <td style={tdStyle}>
                    <span style={statusBadge(item.is_active ? "green" : "slate")}>{item.status || (item.is_active ? "Active" : "Inactive")}</span>
                  </td>
                  <td style={tdStyle}>{formatDate(item.last_login)}</td>
                  <td style={{ ...tdStyle, textAlign: "right" }}>
                    <button
                      type="button"
                      onClick={() => navigate(`/admin/users/${encodeURIComponent(item.student_id || item.user_id)}`)}
                      style={actionButton("primary")}
                    >
                      View
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      ) : null}

      {showCreateModal ? (
        <div style={modalOverlayStyle} role="dialog" aria-modal="true" aria-labelledby="create-user-title">
          <section style={modalCardStyle}>
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "start" }}>
              <div>
                <h2 id="create-user-title" style={{ margin: 0, fontSize: 24 }}>Add User</h2>
                <p style={{ margin: "6px 0 0", color: "var(--muted, #b8b3aa)" }}>Create an admin, tutor, student, or tester account.</p>
              </div>
              <button type="button" onClick={closeCreateModal} style={buttonStyle}>
                Close
              </button>
            </div>

            <form onSubmit={submitCreateUser} style={{ display: "grid", gap: 12, marginTop: 18 }}>
              <label style={modalFieldWrap}>
                <span style={labelStyle}>Full Name</span>
                <input
                  value={createForm.full_name}
                  onChange={(event) => setCreateForm({ ...createForm, full_name: event.target.value })}
                  style={modalInputStyle}
                  required
                />
              </label>
              <label style={modalFieldWrap}>
                <span style={labelStyle}>Email</span>
                <input
                  type="email"
                  value={createForm.email}
                  onChange={(event) => setCreateForm({ ...createForm, email: event.target.value })}
                  style={modalInputStyle}
                  required
                  placeholder="name@example.com"
                />
              </label>
              <label style={modalFieldWrap}>
                <span style={labelStyle}>Password</span>
                <input
                  type="password"
                  value={createForm.password}
                  onChange={(event) => setCreateForm({ ...createForm, password: event.target.value })}
                  style={modalInputStyle}
                  required
                />
              </label>
              <label style={modalFieldWrap}>
                <span style={labelStyle}>Confirm Password</span>
                <input
                  type="password"
                  value={createForm.confirm_password}
                  onChange={(event) => setCreateForm({ ...createForm, confirm_password: event.target.value })}
                  style={modalInputStyle}
                  required
                />
              </label>
              <label style={modalFieldWrap}>
                <span style={labelStyle}>Role</span>
                <select
                  value={createForm.role}
                  onChange={(event) => setCreateForm({ ...createForm, role: event.target.value })}
                  style={modalInputStyle}
                >
                  <option value="student">Student</option>
                  <option value="tutor">Tutor</option>
                  <option value="admin">Admin</option>
                  <option value="tester">Tester</option>
                </select>
              </label>
              <label style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <input
                  type="checkbox"
                  checked={createForm.is_active}
                  onChange={(event) => setCreateForm({ ...createForm, is_active: event.target.checked })}
                />
                <span style={labelStyle}>Active Account</span>
              </label>
              {createError ? <div style={{ ...errorBoxStyle }}>{createError}</div> : null}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", flexWrap: "wrap" }}>
                <button type="button" onClick={closeCreateModal} style={buttonStyle} disabled={creating}>
                  Cancel
                </button>
                <button type="submit" style={primaryButtonStyle} disabled={creating}>
                  {creating ? "Creating..." : "Create User"}
                </button>
              </div>
            </form>
          </section>
        </div>
      ) : null}
    </AdminShell>
  );
}

const fieldStyle = {
  minWidth: 190,
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(8,10,14,.65)",
  color: "#e7e3dc",
};

const buttonStyle = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(255,255,255,.03)",
  color: "inherit",
  cursor: "pointer",
};

const labelStyle = {
  fontSize: 12,
  color: "var(--muted, #b8b3aa)",
  marginBottom: 6,
};

const primaryButtonStyle = {
  ...buttonStyle,
  background: "rgba(201,165,106,.18)",
};

const thStyle = {
  padding: 14,
  borderBottom: "1px solid rgba(255,255,255,.08)",
  fontSize: 12,
  letterSpacing: 0.6,
  textTransform: "uppercase",
};

const tdStyle = {
  padding: 14,
  borderBottom: "1px solid rgba(255,255,255,.06)",
};

const trStyle = {
  borderBottom: "1px solid rgba(255,255,255,.06)",
};

const modalOverlayStyle = {
  position: "fixed",
  inset: 0,
  background: "rgba(0, 0, 0, 0.55)",
  display: "grid",
  placeItems: "center",
  padding: 20,
  zIndex: 50,
};

const modalCardStyle = {
  width: "min(720px, 100%)",
  maxHeight: "90vh",
  overflowY: "auto",
  borderRadius: 24,
  padding: 20,
  background: "rgba(12, 15, 20, 0.98)",
  border: "1px solid rgba(255,255,255,.12)",
  boxShadow: "0 24px 70px rgba(0,0,0,.45)",
};

const modalFieldWrap = {
  display: "grid",
  gap: 6,
};

const modalInputStyle = {
  padding: "10px 12px",
  borderRadius: 12,
  border: "1px solid rgba(255,255,255,.12)",
  background: "rgba(8,10,14,.65)",
  color: "#e7e3dc",
};

const errorBoxStyle = {
  padding: 12,
  borderRadius: 12,
  border: "1px solid rgba(244,67,54,.35)",
  background: "rgba(244,67,54,.12)",
  color: "#ffb1aa",
};

function statusBadge(kind) {
  const base = {
    display: "inline-flex",
    alignItems: "center",
    padding: "4px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0.2,
    border: "1px solid transparent",
    whiteSpace: "nowrap",
  };
  const palette = {
    green: { background: "rgba(76, 175, 80, 0.15)", color: "#9ee6a2", borderColor: "rgba(76, 175, 80, 0.35)" },
    amber: { background: "rgba(255, 193, 7, 0.12)", color: "#ffd977", borderColor: "rgba(255, 193, 7, 0.35)" },
    red: { background: "rgba(244, 67, 54, 0.12)", color: "#ffb1aa", borderColor: "rgba(244, 67, 54, 0.35)" },
    slate: { background: "rgba(148, 163, 184, 0.12)", color: "#d5d9e2", borderColor: "rgba(148, 163, 184, 0.25)" },
  };
  return { ...base, ...(palette[kind] || palette.slate) };
}

function actionButton(kind) {
  if (kind === "primary") {
    return {
      padding: "9px 12px",
      borderRadius: 12,
      border: "1px solid rgba(255,255,255,.12)",
      background: "rgba(201,165,106,.16)",
      color: "#fff",
      cursor: "pointer",
    };
  }
  return buttonStyle;
}
